/**
 * useDictation — speech-to-text via the Web Speech API, ported from Noteble
 * (apps/app/src/components/markdown/useDictation.ts) so the capture app's
 * Record button behaves the same. This gives SystemDox voice capture with no
 * new backend: the browser transcribes locally and the text feeds the same
 * AI-draft / template flow as a typed note. Server-side streaming Transcribe
 * (Noteble parity for native/mobile) remains the heavier follow-up in ADR-022.
 *
 * The pure helpers (support detection, recognition construction, transcript
 * assembly) are exported and unit-tested; the hook is a thin React wrapper.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpeechRecognitionInstance = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

/** Resolve the vendor-prefixed Speech Recognition constructor, if any. */
export function getSpeechRecognitionAPI(
  win: typeof globalThis | undefined = typeof window === 'undefined' ? undefined : window,
): SpeechRecognitionCtor | undefined {
  if (!win) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (win as any).SpeechRecognition || (win as any).webkitSpeechRecognition;
}

export function isSpeechRecognitionSupported(
  win?: typeof globalThis | undefined,
): boolean {
  return !!getSpeechRecognitionAPI(win);
}

/** Concatenate the final segments from a SpeechRecognition result event. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractFinalTranscript(event: any): string {
  let transcript = '';
  for (let i = event.resultIndex; i < event.results.length; i++) {
    if (event.results[i].isFinal) {
      transcript += event.results[i][0].transcript;
    }
  }
  return transcript;
}

interface RecognitionCallbacks {
  onTranscript: (transcript: string) => void;
  onError: () => void;
  onEnd: () => void;
  lang?: string;
}

/** Build and configure a recognition instance; caller invokes .start(). */
export function createDictationRecognition(
  API: SpeechRecognitionCtor,
  { onTranscript, onError, onEnd, lang }: RecognitionCallbacks,
): SpeechRecognitionInstance {
  const recognition = new API();
  recognition.continuous = true;
  recognition.interimResults = false;
  recognition.lang = lang
    || (typeof navigator === 'undefined' ? 'en-US' : navigator.language)
    || 'en-US';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recognition.onresult = (event: any) => {
    const transcript = extractFinalTranscript(event);
    if (transcript) onTranscript(transcript);
  };
  recognition.onerror = onError;
  recognition.onend = onEnd;
  return recognition;
}

/** Append a recognized segment to existing text with sensible spacing. */
export function appendTranscript(existing: string, segment: string): string {
  const clean = segment.trim();
  if (!clean) return existing;
  if (!existing.trim()) return clean.charAt(0).toUpperCase() + clean.slice(1);
  return `${existing.replace(/\s+$/, '')} ${clean}`;
}

interface UseDictationOptions {
  /** Called with each final transcript segment as speech is recognized. */
  onTranscript: (transcript: string) => void;
  /** BCP-47 language tag. Defaults to navigator.language or 'en-US'. */
  lang?: string;
}

interface UseDictationReturn {
  /** Whether dictation is currently active. */
  isListening: boolean;
  /** Whether the browser supports the Speech Recognition API. */
  isSupported: boolean;
  /** Toggle dictation on/off. */
  toggle: () => void;
  /** Force-stop dictation. */
  stop: () => void;
}

export function useDictation({ onTranscript, lang }: UseDictationOptions): UseDictationReturn {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const isSupported = isSpeechRecognitionSupported();

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
      return;
    }

    const API = getSpeechRecognitionAPI();
    if (!API) return;

    const recognition = createDictationRecognition(API, {
      lang,
      onTranscript: (t) => onTranscriptRef.current(t),
      onError: () => stop(),
      onEnd: () => {
        setIsListening(false);
        recognitionRef.current = null;
      },
    });

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, stop, lang]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  return { isListening, isSupported, toggle, stop };
}
